/**
 * @file upp-konva-component.ts
 * @brief Angular host for the floor plan: links {@link UppKonvaModel} with **`upp-konva-floor-plan`** (Konva stage).
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';

import type { UppKonvaModel } from '../model/upp-konva-model';
import type { UppKonvaAreaSource } from '../model/upp-konva-source';
import type { UppKonvaShape } from '../model/upp-konva-shape';
import type { UppKonvaRefresh } from '../model/upp-konva-refresh';
import type { UppKonvaTheme } from '../model/upp-konva-types';
import type { UppKonvaTransform } from '../stage/upp-konva-stage-api';


/**
 * @class UppKonvaComponent
 * Main component that binds a model to a Konva canvas stage.
 *
 * @details
 * **@Input** / **@Output**: `model`, `active`, `theme`, `onChange`, `onSelect` (live shapes from {@link UppKonvaArea.shapes}).
 * In **`ngOnDestroy`**: restore model callbacks and call **`model.doDestroy()`**.
 *
 * @see `model/upp-konva-model.ts`
 */
@Component({
  selector: 'upp-konva',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './upp-konva-component.html',
  styleUrls: ['./upp-konva-component.scss'],
})
export class UppKonvaComponent implements OnChanges, OnDestroy {
  /** @public */
  @Input({ required: true }) model!: UppKonvaModel;

  /** @public Fires on structural change of `shapes` (`onModelChanged`); no payload: host reads `model.area` if needed. */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() readonly onChange = new EventEmitter<void>();

  /**
   * Selection change event emitting the selected shapes.
   * Fires when user selects elements in the floor plan.
   */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() readonly onSelect = new EventEmitter<readonly UppKonvaShape[]>();

  /**
   * VIEW-mode zoom/pan after user navigation on the stage (wheel, pinch, background drag, or zoom API).
   */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() readonly onTransform = new EventEmitter<UppKonvaTransform>();

  /** @public */
  @Input() active = true;

  /** @public Template preset; defaults to light. */
  @Input() theme: UppKonvaTheme = 'UPPKONVA_LIGHTTHEME';

  private _hooked: UppKonvaModel | null = null;
  private _savedOnModelChanged: (() => void) | null = null;
  private _savedUppKonvaRefresh: UppKonvaRefresh | null = null;

  constructor(private readonly _cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']) {
      this._teardownModel();
      if (this.model) {
        this._wireModel();
      }
    }
    if (changes['active'] || changes['theme']) {
      this.model?.doRefresh();
    }
  }

  ngOnDestroy(): void {
    this._teardownModel();
    this.model?.doDestroy();
  }

  /** Handler for floor plan area change from stage. */
  onStageAreaChange(area: UppKonvaAreaSource): void {
    if (this.model.mode === 'EDIT') {
      this.model.pushUndoSnapshot();
    }
    this.model.area.applyAdapterSnapshotFromStage(area);
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(ev: KeyboardEvent): void {
    const undoCombo =
      (ev.ctrlKey || ev.metaKey) && !ev.shiftKey && (ev.key === 'z' || ev.key === 'Z');
    if (!undoCombo || !this.active) {
      return;
    }
    const t = ev.target;
    if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement) {
      return;
    }
    if (t instanceof HTMLElement && t.isContentEditable) {
      return;
    }
    if (!this.model || this.model.mode !== 'EDIT' || !this.model.canUndo) {
      return;
    }
    ev.preventDefault();
    this.model.undo();
    this._cdr.markForCheck();
  }

  /** Handles floor plan element selection from the stage. */
  onFloorPlanSelect(elementUuids: readonly string[]): void {
    const area = this.model.area;
    const shapes = elementUuids
      .map((id) => area.findShapeById(id))
      .filter((s): s is UppKonvaShape => s != null);

    /**
     * Single-select feedback belongs to `onSelected$` on each shape.
     * Multi-selection feedback belongs to `area.onGrouped$`.
     * Avoid emitting both channels for the same user action.
     */
    if (elementUuids.length === 1) {
      for (const shape of shapes) {
        shape.notifySelected();
      }
    }
    this.model.area.notifyHostSelectionFromStage(elementUuids.length);
    this.onSelect.emit(shapes);
  }

  private _wireModel(): void {
    const m = this.model;
    this._savedOnModelChanged = m.onModelChanged;
    m.onModelChanged = () => {
      this._savedOnModelChanged?.();
      this.onChange.emit();
      this._cdr.markForCheck();
    };
    this._savedUppKonvaRefresh = m.uppKonvaRefresh;
    m.uppKonvaRefresh = {
      doRefresh: () => {
        this._savedUppKonvaRefresh?.doRefresh();
        this._cdr.markForCheck();
      },
    };
    this._hooked = m;
    m.doRefresh();
  }

  private _teardownModel(): void {
    if (this._hooked) {
      this._hooked.onModelChanged = this._savedOnModelChanged;
      this._hooked.uppKonvaRefresh = this._savedUppKonvaRefresh;
      this._hooked = null;
      this._savedOnModelChanged = null;
      this._savedUppKonvaRefresh = null;
    }
  }
}
